/* eslint-disable @typescript-eslint/no-explicit-any */
import { Inject, Injectable } from '@angular/core';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import { NotificationMessage } from '@polaris-customerweb/sharedcomponents.garage';
import { Share } from '../models/share';
import { WINDOW } from '../state/app-config/factories/window.factory';

@Injectable({ providedIn: 'root' })
export class BranchService {
    constructor(
        private configService: ConfigService,
        @Inject(WINDOW) private window: Window
    ) {}

    init(options: BranchInitOptions, callback: BranchInitCallback): void {
        this.window.branch.init(
            this.configService.branchKey,
            options,
            callback
        );
    }
}

export interface BranchIo {
    init: (
        key: string,
        options: BranchInitOptions | BranchInitCallback,
        callback?: BranchInitCallback
    ) => void;
}

declare type BranchInitCallback = (err: string, data: BranchInitData) => void;

declare interface BranchInitOptions {
    /**
     * @description The current user's browser-fingerprint-id. The value of this parameter should be the same as the
     * value of ?_branch_match_id (automatically appended by Branch after a link click). _Only necessary if
     * ?_branch_matchid is lost due to multiple redirects in your flow.
     */
    branch_match_id?: string;
    /**
     * @description If you would like to test how Journeys render on your page before activating them, you can set the
     * value of this parameter to the id of the view you are testing. Only necessary when testing a view related to a
     * Journey.
     */
    branch_view_id?: string;
    /** @description When true, prevents Journeys from appearing on current page. */
    no_journeys?: boolean;
    /** @description When true, prevents a Journeys entry animation. */
    disable_entry_animation?: boolean;
    /** @description When true, prevents a Journeys exit animation. */
    disable_exit_animation?: boolean;
    /**
     * @description Whether to try to open the app passively through Journeys (as opposed to opening it upon user
     * clicking); defaults to false.
     */
    open_app?: boolean;
    /**
     * @description A nonce value that will be included on any script or style tags Branch adds to your site. Used to
     * whitelist these tags in your Content Security Policy.
     */
    nonce?: string;
}

export interface BranchInitData {
    data: any;
    /**
     * @description If the user was referred from a link, and the link has associated data, the data is passed in
     * here.
     */
    data_parsed: {
        contactEmail?: string;
        contactId?: string;
        contactNotificationType?: string;
        contactPhone?: string;
        invitecode?: string;
        messageId?: string;
        NavigateTo?: string;
        notificationId?: string;
        notificationType?:
            | 'ContactConfirmationMessage'
            | 'CheckInMessage'
            | 'HelpMessage';
        riderFirstName?: string;
        riderId?: string;
        riderLastName?: string;
        shareId?: string;
        vehicleColor?: string;
        vehicleId?: string;
        vehicleModel?: string;
        vehicleName?: string;
        vehicleYear?: string;
    };
    /** @description Does the user have the app installed already, using Branch's persona data. */
    has_app: boolean;
    /** @description Unique string that identifies the user, if set from setIdentity. */
    identity?: string;
    /** @description The referring link clicked, if available. */
    referring_link: string;
    /**
     * @description If the user was referred from a link, and the link was created by a user with an identity, that
     * identity is here.
     */
    referring_identity: string;
}

export class BranchPayload {
    type: 'unknown';
    payload: any;
}

export class BranchPayloadContactMessage {
    type: 'contactMessage';
    payload: { messageId: string };
}
export class BranchPayloadNotificationMessage {
    type: 'notification';
    payload: NotificationMessage;
}
export class BranchPayloadContactOptConfirmation {
    type: 'contactConfirmation' | 'contactOptOut';
    payload: { contactId: string };
}
export class BranchPayloadShare {
    type: 'share';
    payload: Share;
}
export class BranchPayloadRideGroup {
    type: 'rideGroup';
    payload: { inviteCode: string };
}
function compareType(payload: unknown, key: string): boolean {
    return (
        typeof payload === 'object' &&
        payload !== null &&
        'type' in payload &&
        payload['type'] === key
    );
}
export function payloadIsContactMessage(
    payload: unknown
): payload is BranchPayloadContactMessage {
    return compareType(payload, 'contactMessage');
}
export function payloadIsNotificationMessage(
    payload: unknown
): payload is BranchPayloadNotificationMessage {
    return compareType(payload, 'notification');
}
export function payloadIsContactOptConfirmation(
    payload: unknown
): payload is BranchPayloadContactOptConfirmation {
    return (
        compareType(payload, 'contactConfirmation') ||
        compareType(payload, 'contactOptOut')
    );
}
export function payloadIsShare(
    payload: unknown
): payload is BranchPayloadShare {
    return compareType(payload, 'share');
}
export function payloadIsRideGroup(
    payload: unknown
): payload is BranchPayloadRideGroup {
    return compareType(payload, 'rideGroup');
}
export function payloadIsUnknown(payload: unknown): payload is BranchPayload {
    return compareType(payload, 'unknown');
}
